import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  Mail,
  CreditCard,
  Users,
  Clock,
  Eye,
  Ban,
  Search,
  Loader2,
  BarChart3,
  ArrowUpDown,
  RefreshCw,
  Trash2
} from "lucide-react";

const RISK_COLORS = {
  low: "bg-green-100 text-green-800",
  medium: "bg-amber-100 text-amber-800",
  high: "bg-red-100 text-red-800"
};

const RISK_ICONS = {
  low: CheckCircle2,
  medium: AlertTriangle,
  high: XCircle
};

export default function AccountMonitoringPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [summary, setSummary] = useState({ high_risk: 0, medium_risk: 0, low_risk: 0, suspended: 0 });
  const [total, setTotal] = useState(0);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  
  // Filters
  const [riskFilter, setRiskFilter] = useState("all");
  const [freeEmailOnly, setFreeEmailOnly] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState(-1);
  
  // Detail view
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  
  // Confirm dialogs
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (riskFilter && riskFilter !== "all") params.append("risk_filter", riskFilter);
      if (freeEmailOnly) params.append("free_email_only", "true");
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder.toString());
      params.append("skip", ((currentPage - 1) * pageSize).toString());
      params.append("limit", pageSize.toString());
      
      const response = await axios.get(`${API}/admin/account-monitoring?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBusinesses(response.data.businesses || []);
      setSummary(response.data.summary || { high_risk: 0, medium_risk: 0, low_risk: 0, suspended: 0 });
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load account data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, riskFilter, freeEmailOnly, sortBy, sortOrder, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [riskFilter, freeEmailOnly, sortBy, sortOrder]);
  
  const totalPages = Math.ceil(total / pageSize);

  const fetchDetail = async (businessId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/admin/account-monitoring/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailData(response.data);
    } catch (error) {
      toast.error("Failed to load account details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetail = (business) => {
    setSelectedBusiness(business);
    fetchDetail(business.id);
  };

  const handleSuspend = async () => {
    if (!selectedBusiness) return;
    
    try {
      await axios.put(`${API}/admin/accounts/${selectedBusiness.id}/status`, {
        status: "suspended",
        reason: suspendReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Account suspended");
      setSuspendDialogOpen(false);
      setSuspendReason("");
      setSelectedBusiness(null);
      setDetailData(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to suspend account");
    }
  };

  const handleUnsuspend = async (businessId) => {
    try {
      await axios.put(`${API}/admin/accounts/${businessId}/status`, {
        status: "active"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Account unsuspended");
      if (detailData?.id === businessId) {
        fetchDetail(businessId);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to unsuspend account");
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedBusiness || deleteConfirmText !== "DELETE") return;
    
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/admin/accounts/${selectedBusiness.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Account deleted permanently");
      setDeleteDialogOpen(false);
      setDeleteConfirmText("");
      setSelectedBusiness(null);
      setDetailData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleMarkSafe = async (businessId) => {
    try {
      await axios.post(`${API}/admin/accounts/${businessId}/review`, {
        action: "mark_safe"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Account marked as safe");
      if (detailData?.id === businessId) {
        fetchDetail(businessId);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to mark account as safe");
    }
  };

  const handleMarkUnderReview = async (businessId) => {
    try {
      await axios.post(`${API}/admin/accounts/${businessId}/review`, {
        action: "mark_under_review"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Account marked under review");
      if (detailData?.id === businessId) {
        fetchDetail(businessId);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to mark account under review");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const RiskBadge = ({ risk }) => {
    const Icon = RISK_ICONS[risk] || AlertTriangle;
    return (
      <Badge className={`${RISK_COLORS[risk] || RISK_COLORS.medium} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {risk?.toUpperCase() || "UNKNOWN"}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="account-monitoring-page">
      {/* Summary Cards - Updated to show HIGH/MEDIUM/LOW/Suspended */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50" data-testid="high-risk-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">High Risk</p>
                <p className="text-3xl font-bold text-red-700" data-testid="high-risk-count">{summary.high_risk}</p>
              </div>
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50" data-testid="medium-risk-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Medium Risk</p>
                <p className="text-3xl font-bold text-amber-700" data-testid="medium-risk-count">{summary.medium_risk}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50" data-testid="low-risk-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Low Risk</p>
                <p className="text-3xl font-bold text-green-700" data-testid="low-risk-count">{summary.low_risk}</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-[#e5e5e0]" data-testid="suspended-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#666] font-medium">Suspended</p>
                <p className="text-3xl font-bold text-[#1a4d2e]" data-testid="suspended-count">{summary.suspended}</p>
              </div>
              <Ban className="h-10 w-10 text-[#1a4d2e]/40" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters - Removed Domain Mismatch filter */}
      <Card className="border-[#e5e5e0]">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1a4d2e]" />
              Account Monitoring
            </CardTitle>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-6">
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-40" data-testid="risk-filter-select">
                <SelectValue placeholder="All Risk Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="high">High Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
                <SelectItem value="low">Low Risk</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Checkbox 
                id="freeEmail" 
                checked={freeEmailOnly} 
                onCheckedChange={setFreeEmailOnly}
                data-testid="free-email-checkbox"
              />
              <label htmlFor="freeEmail" className="text-sm">Free Email Only</label>
            </div>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40" data-testid="sort-by-select">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Created Date</SelectItem>
                <SelectItem value="trust_score">Trust Score</SelectItem>
                <SelectItem value="last_login">Last Login</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSortOrder(prev => prev * -1)}
              data-testid="sort-order-btn"
            >
              <ArrowUpDown className="h-4 w-4 mr-1" />
              {sortOrder === -1 ? "Desc" : "Asc"}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1a4d2e]" />
            </div>
          ) : (
            <Table data-testid="accounts-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Email Verified</TableHead>
                  <TableHead>Free Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Subscription Status</TableHead>
                  <TableHead>Referral</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Trust</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Leads (24h)</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businesses.map((biz) => (
                  <TableRow 
                    key={biz.id} 
                    className={biz.account_status === "suspended" ? "bg-red-50" : ""}
                    data-testid={`account-row-${biz.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{biz.business_name}</p>
                        <p className="text-sm text-[#666]">{biz.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {biz.email_verified ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </TableCell>
                    <TableCell>
                      {biz.is_free_email ? (
                        <Badge variant="outline" className="text-amber-600">Yes</Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-600">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium capitalize">{biz.subscription_plan || "—"}</p>
                        <p className="text-xs text-muted-foreground capitalize">{biz.billing_cadence || "—"}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {biz.subscription_status === "active" ? (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      ) : biz.subscription_status === "intro" ? (
                        <Badge className="bg-purple-100 text-purple-800">Trial</Badge>
                      ) : biz.subscription_status === "trialing" ? (
                        <Badge className="bg-blue-100 text-blue-800">Trialing</Badge>
                      ) : biz.subscription_status === "past_due" ? (
                        <Badge className="bg-amber-100 text-amber-800">Past Due</Badge>
                      ) : biz.subscription_status === "canceled" ? (
                        <Badge className="bg-gray-100 text-gray-800">Canceled</Badge>
                      ) : biz.subscription_status === "inactive" ? (
                        <Badge variant="outline">Inactive</Badge>
                      ) : (
                        <Badge variant="outline">{biz.subscription_status || "None"}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {biz.referral_source ? (
                        <Badge variant="outline" className="text-purple-600">{biz.referral_source}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {biz.utm_source ? (
                        <Badge variant="outline" className="text-blue-600">{biz.utm_source}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {biz.utm_campaign ? (
                        <Badge variant="outline" className="text-green-600">{biz.utm_campaign}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono font-bold">{biz.trust_score || 0}</span>
                    </TableCell>
                    <TableCell>
                      <RiskBadge risk={biz.risk_flag} />
                    </TableCell>
                    <TableCell>
                      <span className={biz.leads_24h > 10 ? "text-amber-600 font-bold" : ""}>
                        {biz.leads_24h || 0}
                      </span>
                      <span className="text-[#999]"> / {biz.total_leads || 0}</span>
                    </TableCell>
                    <TableCell className="text-sm text-[#666]">
                      {formatDate(biz.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewDetail(biz)}
                        data-testid={`view-detail-btn-${biz.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination Controls */}
          {!loading && total > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, total)} of {total} accounts
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog - Updated to remove Domain Information section */}
      <Dialog open={!!selectedBusiness} onOpenChange={() => {
        setSelectedBusiness(null);
        setDetailData(null);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Account Details</DialogTitle>
            <DialogDescription>
              {selectedBusiness?.business_name} - {selectedBusiness?.email}
            </DialogDescription>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1a4d2e]" />
            </div>
          ) : detailData && (
            <div className="space-y-6">
              {/* Status Badge */}
              {detailData.account_status === "suspended" && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <Ban className="h-5 w-5" />
                    <span className="font-medium">Account Suspended</span>
                    {detailData.auto_suspended && (
                      <Badge className="bg-red-200 text-red-800 ml-2">Auto-Suspended</Badge>
                    )}
                  </div>
                  {detailData.suspension_reason && (
                    <p className="text-sm text-red-600 mt-1">Reason: {detailData.suspension_reason}</p>
                  )}
                </div>
              )}
              
              {/* Risk Information */}
              <div>
                <h4 className="font-medium mb-3">Risk Assessment</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-xs text-[#666]">Current Risk Level</p>
                    <RiskBadge risk={detailData.risk_flag} />
                  </div>
                  <div className="p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-xs text-[#666]">Trust Score</p>
                    <p className="text-2xl font-bold text-[#1a4d2e]">{detailData.trust_score || 0}</p>
                  </div>
                  <div className="p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-xs text-[#666]">Email Domain</p>
                    <p className="font-mono">{detailData.email_domain || "—"}</p>
                    {detailData.is_free_email && (
                      <Badge variant="outline" className="text-amber-600 mt-1">Free Email</Badge>
                    )}
                  </div>
                  <div className="p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-xs text-[#666]">Subscription Status</p>
                    {detailData.subscription_status === "active" ? (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    ) : detailData.subscription_status === "intro" ? (
                      <Badge className="bg-purple-100 text-purple-800">Trial</Badge>
                    ) : detailData.subscription_status === "trialing" ? (
                      <Badge className="bg-blue-100 text-blue-800">Trialing</Badge>
                    ) : detailData.subscription_status === "past_due" ? (
                      <Badge className="bg-amber-100 text-amber-800">Past Due</Badge>
                    ) : detailData.subscription_status === "canceled" ? (
                      <Badge className="bg-gray-100 text-gray-800">Canceled</Badge>
                    ) : (
                      <Badge variant="outline">{detailData.subscription_status || "Inactive"}</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Plan & Referral Information */}
              <div>
                <h4 className="font-medium mb-3">Plan & Attribution Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-xs text-[#666]">Plan Name</p>
                    <p className="font-medium capitalize">{detailData.subscription_plan || "None"}</p>
                  </div>
                  <div className="p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-xs text-[#666]">Billing Type</p>
                    <p className="font-medium capitalize">{detailData.billing_cadence || "—"}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-xs text-purple-600">Referral Source (who sent them)</p>
                    {detailData.referral_source ? (
                      <Badge className="bg-purple-100 text-purple-800 mt-1">{detailData.referral_source}</Badge>
                    ) : (
                      <p className="text-muted-foreground">—</p>
                    )}
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600">Traffic Source (where they came from)</p>
                    {detailData.utm_source ? (
                      <Badge className="bg-blue-100 text-blue-800 mt-1">{detailData.utm_source}</Badge>
                    ) : (
                      <p className="text-muted-foreground">—</p>
                    )}
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 col-span-2">
                    <p className="text-xs text-green-600">Campaign (why they came)</p>
                    {detailData.utm_campaign ? (
                      <Badge className="bg-green-100 text-green-800 mt-1">{detailData.utm_campaign}</Badge>
                    ) : (
                      <p className="text-muted-foreground">—</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Abuse Indicators */}
              {(detailData.honeypot_trigger_count > 0 || 
                detailData.chargeback_count > 0 || 
                detailData.consecutive_payment_failures > 0 ||
                detailData.captcha_failures_flagged ||
                detailData.automated_behavior_flagged) && (
                <div>
                  <h4 className="font-medium mb-3 text-red-600">Abuse Indicators</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {detailData.honeypot_trigger_count > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-xs text-red-600">Honeypot Triggers</p>
                        <p className="text-xl font-bold text-red-700">{detailData.honeypot_trigger_count}</p>
                      </div>
                    )}
                    {detailData.chargeback_count > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-xs text-red-600">Chargebacks</p>
                        <p className="text-xl font-bold text-red-700">{detailData.chargeback_count}</p>
                      </div>
                    )}
                    {detailData.consecutive_payment_failures > 0 && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-xs text-amber-600">Failed Payments (Consecutive)</p>
                        <p className="text-xl font-bold text-amber-700">{detailData.consecutive_payment_failures}</p>
                      </div>
                    )}
                    {detailData.captcha_failures_flagged && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-xs text-red-600">CAPTCHA Failures</p>
                        <p className="text-sm font-bold text-red-700">Flagged</p>
                      </div>
                    )}
                    {detailData.automated_behavior_flagged && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-xs text-red-600">Automated Behavior</p>
                        <p className="text-sm font-bold text-red-700">Flagged</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Stats */}
              <div>
                <h4 className="font-medium mb-3">Activity Stats</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-2xl font-bold text-[#1a4d2e]">{detailData.total_leads || 0}</p>
                    <p className="text-xs text-[#666]">Total Leads</p>
                  </div>
                  <div className="text-center p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-2xl font-bold text-[#1a4d2e]">{detailData.leads_24h || 0}</p>
                    <p className="text-xs text-[#666]">Leads (24h)</p>
                  </div>
                  <div className="text-center p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-2xl font-bold text-[#1a4d2e]">{detailData.team_member_count || 0}</p>
                    <p className="text-xs text-[#666]">Team Members</p>
                  </div>
                  <div className="text-center p-3 bg-[#f8f8f5] rounded-lg">
                    <p className="text-2xl font-bold text-[#1a4d2e]">{detailData.forms_count || 0}</p>
                    <p className="text-xs text-[#666]">Forms</p>
                  </div>
                </div>
              </div>
              
              {/* Velocity Chart Placeholder */}
              {detailData.velocity_chart && detailData.velocity_chart.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Submission Velocity (Last 24h)</h4>
                  <div className="flex items-end gap-1 h-20 bg-[#f8f8f5] rounded-lg p-2">
                    {detailData.velocity_chart.map((item, idx) => {
                      const maxCount = Math.max(...detailData.velocity_chart.map(i => i.count), 1);
                      const height = (item.count / maxCount) * 100;
                      return (
                        <div
                          key={idx}
                          className="flex-1 bg-[#1a4d2e] rounded-t"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${item.hour}: ${item.count} submissions`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* High Risk Triggers History */}
              {detailData.high_risk_triggers && detailData.high_risk_triggers.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3 text-red-600">High Risk Trigger History</h4>
                  <div className="space-y-2">
                    {detailData.high_risk_triggers.map((trigger, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm border border-red-100">
                        <div>
                          <span className="font-medium text-red-700 capitalize">{trigger.trigger_type?.replace(/_/g, " ")}</span>
                          <span className="text-red-600 ml-2">— {trigger.reason}</span>
                        </div>
                        <span className="text-[#666] text-xs">
                          {formatDate(trigger.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Admin Actions */}
              <div>
                <h4 className="font-medium mb-3">Admin Actions</h4>
                <div className="flex flex-wrap gap-3">
                  {detailData.account_status === "suspended" ? (
                    <Button 
                      variant="outline" 
                      onClick={() => handleUnsuspend(detailData.id)}
                      className="text-green-600"
                      data-testid="unsuspend-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Unsuspend Account
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      onClick={() => setSuspendDialogOpen(true)}
                      className="text-red-600"
                      data-testid="suspend-btn"
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      Suspend Account
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline"
                    onClick={() => handleMarkSafe(detailData.id)}
                    className="text-green-600"
                    data-testid="mark-safe-btn"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Mark Safe
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => handleMarkUnderReview(detailData.id)}
                    className="text-amber-600"
                    data-testid="mark-under-review-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Mark Under Review
                  </Button>
                  
                  <Button 
                    variant="destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              </div>
              
              {/* Recent Admin Actions */}
              {detailData.admin_actions && detailData.admin_actions.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Recent Admin Actions</h4>
                  <div className="space-y-2">
                    {detailData.admin_actions.map((action, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-[#f8f8f5] rounded text-sm">
                        <span className="capitalize">{action.action.replace(/_/g, " ")}</span>
                        <span className="text-[#666]">
                          {action.admin_email} - {formatDate(action.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend Confirmation Dialog */}
      <AlertDialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will prevent the business from logging in and their forms will show "Temporarily unavailable".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              placeholder="Reason for suspension (optional)"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              data-testid="suspend-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSuspend} className="bg-red-600 hover:bg-red-700" data-testid="confirm-suspend-btn">
              Suspend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">⚠️ Delete Account Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3">
                <p className="font-semibold text-red-600">This action is irreversible!</p>
                <p>This will permanently delete:</p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li>The user account</li>
                  <li>All submissions and contacts</li>
                  <li>All forms and configurations</li>
                  <li>All uploaded files</li>
                  <li>All related records</li>
                </ul>
                <p className="pt-2">Type <strong>DELETE</strong> to confirm:</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-2">
            <Input
              placeholder="Type DELETE to confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className={deleteConfirmText === "DELETE" ? "border-red-500" : ""}
              data-testid="delete-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAccount} 
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteConfirmText !== "DELETE" || deletingAccount}
              data-testid="confirm-delete-btn"
            >
              {deletingAccount ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
